import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw } from 'lucide-react';

// Scene definitions.
const SCENES = [
  {
    id: 'flightpath',
    label: 'Flight Path',
    kicker: 'The delivery journey',
    caption: 'Every change follows the same journey — propose, review, gate, release, log. Each stop is visible, timed, and recorded.',
  },
  {
    id: 'regression',
    label: 'Regression Catch',
    kicker: 'Safety nets in action',
    caption: 'A change slips a regression past step 5. Prior tests fail at step 6, the loop returns to a fix — nothing breaks in front of a customer.',
  },
  {
    id: 'gates',
    label: 'Gate Outcomes',
    kicker: 'Risk-based routing',
    caption: 'Every action is classified on four axes and routed to one of four tiers — from auto-proceed to blocked.',
  },
  {
    id: 'boundary',
    label: 'Boundary Hold',
    kicker: 'Human-controlled production',
    caption: 'An agent attempts a production write. The boundary holds — two humans review and release.',
  },
];

/* ─────────────────────────  Scene 1 · Flight Path  ───────────────────────── */
function FlightPathScene() {
  const pathD = 'M 60 400 C 260 400, 400 380, 560 300 S 940 130, 1140 90';
  const markers = [
    { x: 260, y: 383, label: 'Propose', tone: '#6E93B8' },
    { x: 470, y: 335, label: 'Review ★', tone: '#C4633F' },
    { x: 680, y: 265, label: 'Gate', tone: '#5B4FCF' },
    { x: 870, y: 190, label: 'Release ★', tone: '#C4633F' },
    { x: 1070, y: 118, label: 'Ledger', tone: '#178838' },
  ];
  return (
    <>
      {/* protocol bands */}
      <rect x="60" y="54" width="1020" height="34" fill="#22A94F" fillOpacity="0.08" stroke="#22A94F" strokeOpacity="0.35" rx="6" />
      <rect x="60" y="416" width="1080" height="34" fill="#0F3D22" fillOpacity="0.06" stroke="#0F3D22" strokeOpacity="0.3" rx="6" />
      <text x="76" y="75" style={{ fontSize: 11, fill: '#178838', letterSpacing: 1, fontFamily: 'JetBrains Mono, monospace' }}>GOVERNANCE BAND · Reviewed, logged, auditable</text>
      <text x="76" y="437" style={{ fontSize: 11, fill: '#0F3D22', letterSpacing: 1, fontFamily: 'JetBrains Mono, monospace' }}>FLIGHT PLAN · Every requirement reviewed before code exists</text>

      {/* path */}
      <motion.path
        d={pathD}
        stroke="#1A1A1A"
        strokeWidth="2"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.6, ease: 'easeInOut' }}
      />

      {/* markers */}
      {markers.map((m, i) => (
        <g key={i}>
          <motion.circle
            cx={m.x} cy={m.y} r={7} fill={m.tone}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.6 + i * 0.35, duration: 0.4, ease: 'backOut' }}
          />
          <motion.circle
            cx={m.x} cy={m.y} fill={m.tone}
            initial={{ r: 7, opacity: 0.5 }}
            animate={{ r: 20, opacity: 0 }}
            transition={{ delay: 0.6 + i * 0.35, duration: 1.8, repeat: Infinity, repeatDelay: 2.4 }}
          />
          <motion.text
            x={m.x + 12} y={m.y - 10}
            style={{ fontSize: 11, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 + i * 0.35 }}
          >
            {m.label}
          </motion.text>
        </g>
      ))}

      {/* jet glyph */}
      <motion.g
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2.7, duration: 0.6 }}
      >
        <path d="M 1140 80 l 22 6 l -14 8 l -10 12 l -6 -10 l -12 -4 z" fill="#1A1A1A" />
      </motion.g>
    </>
  );
}

/* ─────────────────────────  Scene 2 · Regression Catch  ───────────────────────── */
function RegressionScene() {
  // Eight-step workflow, injecting a regression at step 5 → step 6 fails → loop back to 7 → success at 8.
  const steps = [
    { id: 1, x: 90, label: 'Req', owner: 'dev' },
    { id: 2, x: 220, label: 'Plan', owner: 'agent' },
    { id: 3, x: 350, label: 'Review ★', owner: 'dev', critical: true },
    { id: 4, x: 480, label: 'Tests', owner: 'agent' },
    { id: 5, x: 610, label: 'Impl', owner: 'agent' },
    { id: 6, x: 740, label: 'E2E', owner: 'agent' },
    { id: 7, x: 870, label: 'Fix', owner: 'agent' },
    { id: 8, x: 1010, label: 'Sign-off ★', owner: 'dev', critical: true },
  ];
  const y = 200;

  // Sequence timeline (seconds)
  // 0.0 – 1.4 s: run 1..5 smoothly
  // 1.4 – 2.2 s: step 6 flashes red (regression detected)
  // 2.2 – 3.4 s: loop back arc from 6 → 7
  // 3.4 – 4.4 s: 7 turns amber, then 8 signs off green
  return (
    <>
      {/* base track */}
      <line x1={90} x2={1010} y1={y} y2={y} stroke="#1A1A1A" strokeOpacity={0.2} strokeWidth={1.5} />

      {steps.map((s, i) => {
        const isAgent = s.owner === 'agent';
        const isStep6 = s.id === 6;
        const isStep7 = s.id === 7;
        const isStep8 = s.id === 8;
        return (
          <g key={s.id}>
            {/* node */}
            <motion.circle
              cx={s.x} cy={y} r={16}
              fill={isAgent ? '#6E93B8' : '#C4633F'}
              fillOpacity={0.18}
              stroke={isAgent ? '#6E93B8' : '#C4633F'}
              strokeWidth={1.4}
              strokeDasharray={isAgent ? '4 3' : undefined}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={
                isStep6
                  ? { scale: 1, opacity: 1, fill: ['#6E93B8', '#A63D2F', '#A63D2F', '#B5822A', '#6E93B8'], fillOpacity: [0.18, 0.55, 0.55, 0.4, 0.18] }
                  : isStep7
                  ? { scale: 1, opacity: 1, fill: ['#6E93B8', '#6E93B8', '#B5822A', '#6E93B8'], fillOpacity: [0.18, 0.18, 0.5, 0.18] }
                  : isStep8
                  ? { scale: 1, opacity: 1, fill: ['#C4633F', '#C4633F', '#178838'], fillOpacity: [0.18, 0.18, 0.5] }
                  : { scale: 1, opacity: 1 }
              }
              transition={
                isStep6
                  ? { delay: 0.15 * i, duration: 0.35, opacity: { duration: 0.35 }, fill: { delay: 1.4, duration: 1.2, times: [0, 0.1, 0.55, 0.85, 1] } }
                  : isStep7
                  ? { delay: 0.15 * i, duration: 0.35, fill: { delay: 3.0, duration: 1.4, times: [0, 0.1, 0.55, 1] } }
                  : isStep8
                  ? { delay: 0.15 * i, duration: 0.35, fill: { delay: 3.8, duration: 1.0, times: [0, 0.4, 1] } }
                  : { delay: 0.15 * i, duration: 0.35 }
              }
            />
            <text x={s.x} y={y + 4} style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle' }}>{s.id}</text>
            <text x={s.x} y={y - 26} style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle', opacity: 0.7 }}>{s.label}</text>
            {s.critical && (
              <text x={s.x} y={y + 40} style={{ fontSize: 12, fill: '#178838', textAnchor: 'middle' }}>★</text>
            )}
          </g>
        );
      })}

      {/* the moving execution token */}
      <motion.circle
        r={6} fill="#1A1A1A"
        initial={{ cx: 90, cy: y }}
        animate={{ cx: [90, 220, 350, 480, 610, 740, 740, 870, 870, 1010] }}
        transition={{
          duration: 4.6,
          times: [0, 0.11, 0.22, 0.33, 0.44, 0.55, 0.65, 0.76, 0.85, 1.0],
          ease: 'easeInOut',
          repeat: Infinity,
          repeatDelay: 1.2,
        }}
      />

      {/* loop-back arc from step 6 to step 7 */}
      <motion.path
        d={`M 740 ${y - 20} Q 805 130 870 ${y - 20}`}
        stroke="#A63D2F"
        strokeWidth={1.8}
        fill="none"
        strokeDasharray="6 4"
        initial={{ opacity: 0, pathLength: 0 }}
        animate={{ opacity: [0, 1, 1, 0], pathLength: [0, 1, 1, 0] }}
        transition={{ delay: 2.2, duration: 3.4, times: [0, 0.15, 0.85, 1], repeat: Infinity, repeatDelay: 1.2 }}
      />
      <motion.text
        x={805} y={110}
        style={{ fontSize: 11, fill: '#A63D2F', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{ delay: 2.2, duration: 3.4, times: [0, 0.15, 0.85, 1], repeat: Infinity, repeatDelay: 1.2 }}
      >
        prior test failed · loop back
      </motion.text>

      {/* status readout */}
      <text x={90} y={330} style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1 }}>
        <tspan opacity={0.6}>STATUS · </tspan>
        <tspan fill="#178838">the safety net catches what step 5 missed.</tspan>
      </text>
    </>
  );
}

/* ─────────────────────────  Scene 3 · Gate Outcomes  ───────────────────────── */
function GatesScene() {
  const tiers = [
    { tier: 1, y: 90,  color: '#22A94F', name: 'Proceed', behaviour: 'Executes. Logged.' },
    { tier: 2, y: 175, color: '#B5822A', name: 'Notify + proceed', behaviour: 'Executes. Cancel window opens.' },
    { tier: 3, y: 260, color: '#5B4FCF', name: 'Wait for approval', behaviour: 'Queued. Timeout cancels.' },
    { tier: 4, y: 345, color: '#A63D2F', name: 'Block', behaviour: 'Refused. Agent re-plans.' },
  ];
  return (
    <>
      {/* incoming action */}
      <motion.g
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <rect x="80" y="200" width="120" height="42" rx="8" fill="#F4F2EC" stroke="#1A1A1A" strokeOpacity="0.25" />
        <text x="140" y="226" style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle' }}>agent action</text>
      </motion.g>

      {/* classifier */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <rect x="260" y="180" width="180" height="86" rx="10" fill="#EDEBFB" stroke="#5B4FCF" strokeOpacity="0.5" />
        <text x="350" y="205" style={{ fontSize: 10, fill: '#5B4FCF', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle', letterSpacing: 1 }}>CLASSIFIER · 4 AXES</text>
        <text x="278" y="228" style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace' }}>type · scope</text>
        <text x="278" y="248" style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace' }}>reversibility · novelty</text>
      </motion.g>

      {/* fan-out lines */}
      {tiers.map((t, i) => (
        <motion.path
          key={t.tier}
          d={`M 440 223 Q 540 ${(223 + t.y + 20) / 2} 620 ${t.y + 20}`}
          stroke={t.color}
          strokeWidth={1.5}
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ delay: 0.9 + i * 0.15, duration: 0.6 }}
        />
      ))}

      {/* tier rows */}
      {tiers.map((t, i) => (
        <motion.g
          key={t.tier}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.3 + i * 0.15, duration: 0.5 }}
        >
          <rect x="620" y={t.y} width="480" height="42" rx="8" fill={t.color} fillOpacity="0.1" stroke={t.color} strokeOpacity="0.55" />
          <circle cx="642" cy={t.y + 21} r="9" fill={t.color} />
          <text x="642" y={t.y + 25} style={{ fontSize: 11, fill: '#fff', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle', fontWeight: 600 }}>{t.tier}</text>
          <text x="666" y={t.y + 18} style={{ fontSize: 13, fill: '#1A1A1A', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{t.name}</text>
          <text x="666" y={t.y + 34} style={{ fontSize: 11, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', opacity: 0.65 }}>{t.behaviour}</text>

          {/* animated behaviour indicator */}
          {t.tier === 1 && (
            <motion.circle cx="1080" cy={t.y + 21} r="5" fill={t.color}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 1] }}
              transition={{ delay: 2.1, duration: 0.6 }} />
          )}
          {t.tier === 2 && (
            <motion.g>
              <motion.rect x="1050" y={t.y + 12} width="60" height="18" rx="9" fill={t.color} fillOpacity="0.2" stroke={t.color}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.3 }} />
              <motion.rect x="1050" y={t.y + 12} height="18" rx="9" fill={t.color}
                initial={{ width: 60, opacity: 0 }}
                animate={{ width: [60, 0], opacity: [0.7, 0.7] }}
                transition={{ delay: 2.3, duration: 2, ease: 'linear', repeat: Infinity }}
              />
            </motion.g>
          )}
          {t.tier === 3 && (
            <motion.g>
              <motion.circle cx="1085" cy={t.y + 21} r="4" fill={t.color}
                initial={{ opacity: 0.3 }}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ delay: 2.5, duration: 1.4, repeat: Infinity }} />
              <text x="1050" y={t.y + 25} style={{ fontSize: 10, fill: t.color, fontFamily: 'JetBrains Mono, monospace' }}>wait...</text>
            </motion.g>
          )}
          {t.tier === 4 && (
            <motion.g
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 2.7, type: 'spring', stiffness: 200 }}
            >
              <line x1="1074" y1={t.y + 12} x2="1092" y2={t.y + 30} stroke={t.color} strokeWidth="2.4" strokeLinecap="round" />
              <line x1="1092" y1={t.y + 12} x2="1074" y2={t.y + 30} stroke={t.color} strokeWidth="2.4" strokeLinecap="round" />
            </motion.g>
          )}
        </motion.g>
      ))}

      {/* hard guard note */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.4 }}
      >
        <rect x="80" y="410" width="1020" height="34" rx="6" fill="#1A1A1A" fillOpacity="0.04" stroke="#1A1A1A" strokeOpacity="0.15" strokeDasharray="4 3" />
        <text x="98" y="431" style={{ fontSize: 11, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1 }}>
          <tspan fill="#A63D2F">HARD SCOPE GUARDS · </tspan>
          <tspan opacity={0.7}>evaluated before classification. Trip a guard, it is blocked regardless of tier.</tspan>
        </text>
      </motion.g>
    </>
  );
}

/* ─────────────────────────  Scene 4 · Boundary Hold  ───────────────────────── */
function BoundaryScene() {
  return (
    <>
      {/* Non-production zone (dashed) */}
      <motion.rect
        x="60" y="90" width="450" height="300" rx="16"
        fill="#6E93B8" fillOpacity="0.06"
        stroke="#6E93B8" strokeOpacity="0.55" strokeWidth="1.5" strokeDasharray="8 4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
      />
      <text x="80" y="118" style={{ fontSize: 11, fill: '#6E93B8', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1 }}>AGENT-OWNED · non-production</text>
      <text x="200" y="200" style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace' }}>test</text>
      <text x="200" y="240" style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace' }}>epic-x</text>
      <text x="200" y="280" style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace' }}>shadow-data</text>

      {/* Production zone (solid) */}
      <motion.rect
        x="820" y="90" width="320" height="300" rx="16"
        fill="#A63D2F" fillOpacity="0.06"
        stroke="#A63D2F" strokeOpacity="0.6" strokeWidth="1.8"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}
      />
      <text x="840" y="118" style={{ fontSize: 11, fill: '#A63D2F', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1 }}>PRODUCTION · human-controlled</text>
      <text x="960" y="220" style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle' }}>customer data</text>
      <text x="960" y="245" style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle' }}>live systems</text>

      {/* boundary line */}
      <line x1="665" y1="80" x2="665" y2="400" stroke="#1A1A1A" strokeOpacity="0.2" strokeDasharray="3 4" />
      <text x="665" y="72" style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle', letterSpacing: 1 }}>BOUNDARY</text>

      {/* Agent attempts direct write → boundary hold (flashes red X) */}
      <motion.circle
        cx={280} cy={195} r="9" fill="#6E93B8"
        initial={{ cx: 280 }}
        animate={{ cx: [280, 620, 620, 280] }}
        transition={{ duration: 3.2, times: [0, 0.35, 0.5, 1], repeat: Infinity, repeatDelay: 1.5, ease: 'easeInOut' }}
      />
      <motion.g
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.6, 1.1, 1, 0.6] }}
        transition={{ duration: 3.2, times: [0, 0.4, 0.55, 0.7], repeat: Infinity, repeatDelay: 1.5 }}
      >
        <circle cx="665" cy="195" r="14" fill="#A63D2F" fillOpacity="0.16" />
        <line x1="656" y1="186" x2="674" y2="204" stroke="#A63D2F" strokeWidth="2.4" strokeLinecap="round" />
        <line x1="674" y1="186" x2="656" y2="204" stroke="#A63D2F" strokeWidth="2.4" strokeLinecap="round" />
      </motion.g>

      {/* Correct path: through the review pipeline (below) */}
      <motion.path
        d="M 280 240 Q 280 340 500 340 T 900 340 L 900 260"
        stroke="#178838"
        strokeWidth="1.8"
        fill="none"
        strokeDasharray="6 3"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ delay: 1.2, duration: 1.4 }}
      />
      <motion.circle
        r="6" fill="#178838"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1] }}
        transition={{ delay: 1.5, duration: 0.4 }}
      >
        <animateMotion dur="3.8s" begin="1.5s" repeatCount="indefinite" path="M 280 240 Q 280 340 500 340 T 900 340 L 900 260" />
      </motion.circle>

      {/* Two humans along the path */}
      {[
        { x: 540, label: 'Reviewer 1', title: 'reviews plan' },
        { x: 780, label: 'Reviewer 2', title: 'approves & releases' },
      ].map((r, i) => (
        <motion.g
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8 + i * 0.4, duration: 0.4 }}
        >
          <circle cx={r.x} cy={340} r="16" fill="#C4633F" fillOpacity="0.15" stroke="#C4633F" strokeWidth="1.4" />
          <circle cx={r.x} cy={335} r="4" fill="#C4633F" />
          <path d={`M ${r.x - 6} 344 Q ${r.x} 350 ${r.x + 6} 344`} stroke="#C4633F" strokeWidth="1.4" fill="none" />
          <text x={r.x} y={380} style={{ fontSize: 10, fill: '#C4633F', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle' }}>{r.label}</text>
          <text x={r.x} y={393} style={{ fontSize: 9, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle', opacity: 0.65 }}>{r.title}</text>
        </motion.g>
      ))}

      <motion.text
        x={600} y={438}
        style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', textAnchor: 'middle', letterSpacing: 1 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.0 }}
      >
        <tspan opacity={0.6}>THE RULE · </tspan>
        <tspan fill="#178838">agents propose and prepare. Two different people review and release.</tspan>
      </motion.text>
    </>
  );
}

/* ─────────────────────────  Main component  ───────────────────────── */
export default function FlightPathViz({ variant = 'full' }) {
  const [sceneIndex, setSceneIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [nonce, setNonce] = useState(0); // used to force scene re-mount for replay

  // Auto-advance scenes when playing
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      setSceneIndex((i) => (i + 1) % SCENES.length);
      setNonce((n) => n + 1);
    }, 8000);
    return () => clearTimeout(t);
  }, [sceneIndex, playing]);

  const height = variant === 'condensed' ? 340 : 500;
  const current = SCENES[sceneIndex];

  const goto = (i) => {
    setSceneIndex(i);
    setNonce((n) => n + 1);
  };

  return (
    <div className="relative w-full rounded-[28px] bg-[#FBFAF6] border border-[#E8E4D9] overflow-hidden">
      {/* Header bar with scene tabs and playback controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-[#E8E4D9] bg-[#F4F2EC]/60">
        <div className="flex flex-wrap items-center gap-1.5">
          {SCENES.map((s, i) => {
            const active = i === sceneIndex;
            return (
              <button
                key={s.id}
                onClick={() => goto(i)}
                className={`px-3.5 py-2 rounded-full text-[11.5px] tracking-tight font-medium transition-colors ${active ? 'bg-[#22A94F] text-[#0F3D22]' : 'bg-transparent text-[#1A1A1A]/65 hover:bg-[#E8E4D9] hover:text-[#1A1A1A]'}`}
              >
                <span className="mono mr-1.5 opacity-60">0{i + 1}</span>
                {s.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setNonce((n) => n + 1)}
            className="p-1.5 rounded-full text-[#1A1A1A]/60 hover:bg-[#E8E4D9] hover:text-[#22A94F]"
            aria-label="Replay scene"
          >
            <RotateCcw size={14} />
          </button>
          <button
            onClick={() => setPlaying((p) => !p)}
            className="p-1.5 rounded-full text-[#1A1A1A]/60 hover:bg-[#E8E4D9] hover:text-[#22A94F]"
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause size={14} /> : <Play size={14} />}
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="relative" style={{ height }}>
        <svg viewBox="0 0 1200 460" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
          {/* grid */}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={0} x2={1200} y1={40 + i * 45} y2={40 + i * 45} stroke="#1A1A1A" strokeOpacity={0.04} />
          ))}
          <line x1={0} x2={1200} y1={410} y2={410} stroke="#1A1A1A" strokeOpacity={0.15} strokeDasharray="6 8" />

          <AnimatePresence mode="wait">
            <motion.g
              key={`${current.id}-${nonce}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              {current.id === 'flightpath' && <FlightPathScene />}
              {current.id === 'regression' && <RegressionScene />}
              {current.id === 'gates' && <GatesScene />}
              {current.id === 'boundary' && <BoundaryScene />}
            </motion.g>
          </AnimatePresence>
        </svg>

        {/* corner legend */}
        <div className="absolute top-4 left-6 flex items-center gap-3 text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/55">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22A94F] pulse-soft" />
          <span>{current.label}</span>
          <span className="text-[#1A1A1A]/30">·</span>
          <span className="text-[#1A1A1A]/40">Scene {sceneIndex + 1} / {SCENES.length}</span>
        </div>
      </div>

      {/* Caption */}
      <div className="px-6 py-5 border-t border-[#E8E4D9] bg-[#F4F2EC]/60 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <div className="flex-shrink-0">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#22A94F]">
            Scene {String(sceneIndex + 1).padStart(2, '0')} · {current.label}
          </div>
          <div className="serif text-[18px] leading-tight text-[#1A1A1A] mt-1">
            {current.kicker}
          </div>
        </div>
        <p className="text-[13.5px] leading-relaxed text-[#1A1A1A]/75 md:border-l md:border-[#E8E4D9] md:pl-6">
          {current.caption}
        </p>
      </div>
    </div>
  );
}
